import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { AREAS_SUGERIDAS, TIPOS_BOT, TIPO_META } from '../constants';
import type { ICadastrarBot, TipoBot } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ICadastrarBot) => void;
  isSaving: boolean;
}

// Mesma regra do backend (_validate_name): o nome vira chave e segmento de URL.
const NOME_VALIDO = /^[a-zA-Z0-9_.-]+$/;

export const CadastrarBotModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, isSaving }) => {
  const [botName, setBotName] = useState('');
  const [tipoBot, setTipoBot] = useState<TipoBot>('ETL');
  const [apelido, setApelido] = useState('');
  const [descricao, setDescricao] = useState('');
  const [area, setArea] = useState('');
  const [tocado, setTocado] = useState(false);

  const nomeInvalido = tocado && !NOME_VALIDO.test(botName);

  const limpar = () => {
    setBotName('');
    setTipoBot('ETL');
    setApelido('');
    setDescricao('');
    setArea('');
    setTocado(false);
  };

  const fechar = () => {
    limpar();
    onClose();
  };

  const submeter = () => {
    setTocado(true);
    if (!NOME_VALIDO.test(botName)) return;
    onSubmit({
      bot_name: botName.trim(),
      tipo_bot: tipoBot,
      apelido: apelido.trim() || undefined,
      descricao: descricao.trim() || undefined,
      area: area || undefined,
    });
    limpar();
  };

  return (
    <Modal isOpen={isOpen} onClose={fechar} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cadastrar bot</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={nomeInvalido}>
              <FormLabel fontSize="sm">Nome técnico</FormLabel>
              <Input
                value={botName}
                onChange={e => setBotName(e.target.value)}
                onBlur={() => setTocado(true)}
                placeholder="BOT_APURACAO_SN"
                fontFamily="mono"
              />
              {nomeInvalido ? (
                <FormErrorMessage>
                  Use apenas letras, números, ponto, hífen e underline.
                </FormErrorMessage>
              ) : (
                <FormHelperText>
                  É a identidade do bot no código — não muda depois. Sem espaços.
                </FormHelperText>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Tipo</FormLabel>
              <Select value={tipoBot} onChange={e => setTipoBot(e.target.value as TipoBot)}>
                {TIPOS_BOT.map(t => (
                  <option key={t} value={t}>
                    {TIPO_META[t].label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Apelido</FormLabel>
              <Input
                value={apelido}
                onChange={e => setApelido(e.target.value)}
                placeholder="Apuração Simples Nacional"
              />
              <FormHelperText>
                Como aparece nas telas para quem não é do time técnico.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Área</FormLabel>
              <Select value={area} onChange={e => setArea(e.target.value)} placeholder="Selecione">
                {AREAS_SUGERIDAS.map(a => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Descrição</FormLabel>
              <Textarea
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                placeholder="O que este bot faz, e o que quebra quando ele falha."
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={submeter}
            isLoading={isSaving}
            isDisabled={!botName.trim()}
          >
            Cadastrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
